import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { sanitizeHTML } from '../../utils/sanitizeHTML';
import { Border, Grid, Shadow, Type } from '../../app/StyleGuide';
import { RichTextToolbar, ToolbarButton } from './RichTextToolbar';
import { useRichTextEditor } from './useRichTextEditor';
import ErrorMessage from '../ErrorMessage';

type InlineEditableTextEditorProps = {
  text: string;
  onChangeText: (newText: string) => void;
  className?: string;
  multiline?: boolean;
  onDeleteCustomization?: () => void;
  editable: boolean;
  allowedTags?: ('strong' | 'em' | 'u' | 'ul' | 'ol' | 'li' | 'p' | 'br' | 'span' | 'a' | 'img')[];
  allowedAttributes?: string[];
};

const EditableContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  cursor: pointer;
  margin-left: -${Grid._3};
  margin-bottom: -${Grid._1};
`;

const TextDisplay = styled.div<{ customizable: boolean }>`
  padding: ${Grid._1} ${Grid._3};
  border-radius: ${Border.radius};
  ${props =>
    props.customizable &&
    `
    &:hover {
    background-color: rgba(0, 0, 0, 0.05);
  }
  `}
`;

const RichTextEditorContainer = styled.div`
  padding: ${Grid._1} ${Grid._2};
  border: 1px solid #ccc;
  border-radius: ${Border.radius};
  font-size: inherit;
  font-family: inherit;
  background: rgba(0, 0, 0, 0.05);
  outline: none;
  min-height: 60px;
  width: 100%;
  font-weight: ${Type.Weight.semibold};
  &:focus {
    border-color: transparent;
    box-shadow: ${Shadow.regular};
  }

  img {
    max-width: 100%;
    height: auto;
  }
`;

// Needed to support image uploads in Safari
const HiddenFileInput = styled.input`
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  border: 0;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
`;

export const InlineEditableTextEditor = ({
  text,
  onChangeText,
  className,
  multiline = false,
  onDeleteCustomization,
  editable,
  allowedTags = [],
  allowedAttributes = [],
}: InlineEditableTextEditorProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [inputValue, setInputValue] = useState(text);
  const [isHovered, setIsHovered] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setInputValue(text);
  }, [text]);

  const handleEditorChange = useCallback((newValue: string) => {
    setInputValue(newValue);
  }, []);

  const {
    inputRef,
    imageInputRef,
    hasRichTextTags,
    errorMessage,
    handleInput,
    handlePaste,
    handleFormatText,
    handleInsertLink,
    handleInsertImage,
    handleImageFilesSelected,
  } = useRichTextEditor({
    value: inputValue,
    onChange: handleEditorChange,
    allowedTags,
    allowedAttributes,
  });

  const [isToolbarVisible, setIsToolbarVisible] = useState(false);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      (inputRef.current as HTMLDivElement).focus();
      setIsToolbarVisible(true);
      const sanitizedValue = sanitizeHTML(inputValue);
      if ((inputRef.current as HTMLDivElement).innerHTML !== sanitizedValue) {
        (inputRef.current as HTMLDivElement).innerHTML = sanitizedValue;
      }
    }
  }, [isEditing, inputRef, inputValue]);

  const handleSave = useCallback(() => {
    const currentValue = inputRef.current ? (inputRef.current as HTMLDivElement).innerHTML : inputValue;
    const sanitizedCurrentValue = sanitizeHTML(currentValue);
    const sanitizedText = sanitizeHTML(text);

    setIsEditing(false);
    setIsToolbarVisible(false);
    if (sanitizedCurrentValue !== sanitizedText) {
      onChangeText(currentValue);
    }
  }, [inputValue, text, onChangeText, inputRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (isEditing && containerRef.current && !containerRef.current.contains(event.target as Node)) {
        handleSave();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isEditing, handleSave]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleDeleteCustomization = () => {
    setIsEditing(false);
    setInputValue(text);
    setIsToolbarVisible(false);
    onDeleteCustomization?.();
  };

  const handleCancel = () => {
    setIsEditing(false);
    setInputValue(text);
    setIsToolbarVisible(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement | HTMLDivElement>) => {
    if (e.key === 'Enter' && !multiline && !hasRichTextTags) {
      handleSave();
    } else if (e.key === 'Escape') {
      handleCancel();
    }
  };

  const formatText = (command: 'bold' | 'italic' | 'underline' | 'insertUnorderedList' | 'insertOrderedList') => {
    if (!hasRichTextTags) return;
    handleFormatText(command);
  };

  if (!editable) {
    return (
      <TextDisplay
        className={className}
        dangerouslySetInnerHTML={{ __html: sanitizeHTML(text) }}
        customizable={editable}
      />
    );
  }

  return (
    <EditableContainer
      className={className}
      ref={containerRef}
      onMouseEnter={() => {
        setIsHovered(true);
      }}
      onMouseLeave={() => {
        setIsHovered(false);
      }}
    >
      <HiddenFileInput
        ref={imageInputRef}
        type="file"
        accept="image/*"
        multiple
        tabIndex={-1}
        aria-hidden="true"
        onChange={handleImageFilesSelected}
        data-qa-hook="rich-text-editor-image-input"
      />
      <RichTextToolbar isVisible={isToolbarVisible || isHovered} inline={false} role="toolbar">
        {!isEditing ? (
          <>
            <ToolbarButton onClick={handleClick} title="Edit">
              <i className="fas fa-pencil-alt"></i>
            </ToolbarButton>
            {onDeleteCustomization && (
              <ToolbarButton onClick={handleDeleteCustomization} title="Delete Customization">
                <i className="fas fa-undo"></i>
              </ToolbarButton>
            )}
          </>
        ) : (
          <>
            {hasRichTextTags && (
              <>
                {allowedTags.includes('strong') && (
                  <ToolbarButton onClick={() => formatText('bold')} aria-label="Bold" title="Bold">
                    <i className="fas fa-bold"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('em') && (
                  <ToolbarButton onClick={() => formatText('italic')} aria-label="Italic" title="Italic">
                    <i className="fas fa-italic"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('u') && (
                  <ToolbarButton onClick={() => formatText('underline')} aria-label="Underline" title="Underline">
                    <i className="fas fa-underline"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('ul') && (
                  <ToolbarButton
                    onClick={() => formatText('insertUnorderedList')}
                    aria-label="Bullet List"
                    title="Bullet List"
                  >
                    <i className="fas fa-list-ul"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('ol') && (
                  <ToolbarButton
                    onClick={() => formatText('insertOrderedList')}
                    aria-label="Numbered List"
                    title="Numbered List"
                  >
                    <i className="fas fa-list-ol"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('a') && (
                  <ToolbarButton onClick={handleInsertLink} aria-label="Insert Link" title="Insert Link">
                    <i className="fas fa-link"></i>
                  </ToolbarButton>
                )}
                {allowedTags.includes('img') && (
                  <ToolbarButton onClick={handleInsertImage} aria-label="Insert Image" title="Insert Image">
                    <i className="fas fa-image"></i>
                  </ToolbarButton>
                )}
              </>
            )}
            <>
              <ToolbarButton onClick={handleSave} aria-label="Save" title="Save" style={{ color: '#09bc2d' }}>
                <i className="fas fa-check"></i>
              </ToolbarButton>
              <ToolbarButton onClick={handleCancel} aria-label="Cancel" title="Cancel" style={{ color: '#ff1010' }}>
                <i className="fas fa-times"></i>
              </ToolbarButton>
            </>
          </>
        )}
      </RichTextToolbar>

      {isEditing ? (
        <>
          <RichTextEditorContainer
            ref={inputRef as React.RefObject<HTMLDivElement>}
            contentEditable
            suppressContentEditableWarning
            onInput={handleInput}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
          {errorMessage && (
            <ErrorMessage align="left" data-qa-hook="rich-text-editor-error">
              {errorMessage}
            </ErrorMessage>
          )}
        </>
      ) : (
        <TextDisplay
          className={className}
          onDoubleClick={handleClick}
          dangerouslySetInnerHTML={{ __html: sanitizeHTML(text) }}
          customizable={true}
        />
      )}
    </EditableContainer>
  );
};
